import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Eraser } from 'lucide-react';

// Lightweight draw-to-sign canvas (pointer events, works with mouse + touch). Exposes the drawn
// signature via getDataUrl() on the ref passed in `apiRef`, and isEmpty().
export function SignaturePad({ apiRef, height = 160 }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1F2937';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, height);

    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return { x: p.clientX - r.left, y: p.clientY - r.top };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
    const move = (e) => {
      if (!drawing.current) return;
      e.preventDefault();
      const p = pos(e);
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      last.current = p;
      dirty.current = true;
    };
    const end = () => { drawing.current = false; };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);

    if (apiRef) {
      apiRef.current = {
        getDataUrl: () => (dirty.current ? canvas.toDataURL('image/png') : null),
        isEmpty: () => !dirty.current,
      };
    }
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [apiRef, height]);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, height);
    dirty.current = false;
  };

  return (
    <div className="space-y-1.5">
      <canvas
        ref={canvasRef}
        data-testid="signature-pad-canvas"
        style={{ width: '100%', height, touchAction: 'none' }}
        className="rounded-lg border border-border/60 bg-white cursor-crosshair"
      />
      <Button type="button" variant="ghost" size="sm" data-testid="signature-clear-btn" onClick={clear} className="text-muted-ink gap-1.5">
        <Eraser size={14} /> Clear
      </Button>
    </div>
  );
}
